import { Plus, Trash2 } from 'lucide-react';
import { useCallback } from 'react';
import { MultiImageUploader } from './MultiImageUploader';

export interface RenovationOption {
  name: string;
  estimatedCost: number;
  images: string[];
  description?: string;
}

interface RenovationOptionsEditorProps {
  value: RenovationOption[];
  onChange: (options: RenovationOption[]) => void;
  entityId?: string;
  label?: string;
  maxOptions?: number;
  className?: string;
}

export function RenovationOptionsEditor({
  value = [],
  onChange,
  entityId,
  label = 'Renovation Options',
  maxOptions = 5,
  className = '',
}: RenovationOptionsEditorProps) {
  const handleAddOption = useCallback(() => {
    if (value.length >= maxOptions) return;

    onChange([
      ...value,
      {
        name: `Renovation Option ${value.length + 1}`,
        estimatedCost: 0,
        images: [],
        description: '',
      },
    ]);
  }, [value, onChange, maxOptions]);

  const handleRemoveOption = useCallback(
    (index: number) => {
      const newValue = [...value];
      newValue.splice(index, 1);
      onChange(newValue);
    },
    [value, onChange]
  );

  const handleUpdateOption = useCallback(
    (index: number, updates: Partial<RenovationOption>) => {
      const newValue = [...value];
      const current = newValue[index];
      if (current) {
        const updated: RenovationOption = {
          name: updates.name !== undefined ? updates.name : current.name,
          estimatedCost:
            updates.estimatedCost !== undefined ? updates.estimatedCost : current.estimatedCost,
          images: updates.images !== undefined ? updates.images : current.images,
          description:
            updates.description !== undefined ? updates.description : current.description,
        };
        newValue[index] = updated;
        onChange(newValue);
      }
    },
    [value, onChange]
  );

  const canAddMore = value.length < maxOptions;

  return (
    <div className={className}>
      {label && (
        <div className="mb-[12px] flex items-center justify-between">
          <span className="text-[14px] font-medium text-[#181818]">
            {label}
            <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
              ({value.length}/{maxOptions})
            </span>
          </span>
          {canAddMore && (
            <button
              type="button"
              onClick={handleAddOption}
              className="flex items-center gap-[4px] rounded-[6px] bg-[#055135] px-[12px] py-[6px] text-[12px] font-medium text-white transition-colors hover:bg-[#044128]"
            >
              <Plus size={14} />
              Add Option
            </button>
          )}
        </div>
      )}

      {value.length === 0 ? (
        <div className="rounded-[8px] border-2 border-dashed border-[#e5e5e5] p-[24px] text-center">
          <p className="text-[14px] text-[#8d8c8f]">No renovation options added yet</p>
          {canAddMore && (
            <button
              type="button"
              onClick={handleAddOption}
              className="mt-[12px] inline-flex items-center gap-[4px] text-[14px] font-medium text-[#055135] hover:underline"
            >
              <Plus size={16} />
              Add your first option
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-[16px]">
          {value.map((option, index) => (
            <div
              key={`option-${option.name}-${index}`}
              className="rounded-[12px] border border-[#e5e5e5] bg-white"
            >
              <div className="flex items-center justify-between border-b border-[#e5e5e5] bg-[#f5f5f5] px-[16px] py-[12px]">
                <span className="text-[14px] font-semibold text-[#181818]">Option {index + 1}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] text-[#646464] transition-colors hover:bg-[#fee] hover:text-[#d4183d]"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="space-y-[16px] p-[16px]">
                <div className="grid grid-cols-2 gap-[16px]">
                  <label className="block">
                    <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                      Option Name
                    </span>
                    <input
                      type="text"
                      value={option.name}
                      onChange={(e) => handleUpdateOption(index, { name: e.target.value })}
                      className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                      placeholder="e.g., Modern Kitchen Upgrade"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                      Estimated Cost ($)
                    </span>
                    <input
                      type="number"
                      value={option.estimatedCost || ''}
                      onChange={(e) =>
                        handleUpdateOption(index, {
                          estimatedCost: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                      placeholder="50000"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                    Description
                  </span>
                  <textarea
                    value={option.description || ''}
                    onChange={(e) => handleUpdateOption(index, { description: e.target.value })}
                    rows={2}
                    className="w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] py-[8px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                    placeholder="Describe this renovation option..."
                  />
                </label>

                <MultiImageUploader
                  value={option.images}
                  onChange={(images) => handleUpdateOption(index, { images })}
                  folder="listings/renovation-options"
                  entityId={entityId}
                  label="Option Images"
                  maxImages={6}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
